/**
 * pixi-particles — GravityWell.ts
 * Ported from Phaser.GameObjects.Particles.GravityWell
 *
 * Applies a force on particles to draw them towards, or repel them from, a
 * single point. Force is inversely proportional to the square of the
 * distance from the particle to the point (Newton's law of gravity).
 */
import { ParticleProcessor } from './ParticleProcessor';
import { GetFastValue } from './Utils';
import { AnyParticle } from './EmitterOp';

export interface GravityWellConfig
{
    x?: number;
    y?: number;
    power?: number;
    epsilon?: number;
    gravity?: number;
}

export class GravityWell extends ParticleProcessor
{
    private _gravity: number;
    private _power: number;
    private _epsilon: number;

    constructor (x?: number | GravityWellConfig, y?: number, power?: number, epsilon?: number, gravity?: number)
    {
        if (typeof x === 'object')
        {
            var config = x;

            x = GetFastValue(config, 'x', 0);
            y = GetFastValue(config, 'y', 0);
            power = GetFastValue(config, 'power', 0);
            epsilon = GetFastValue(config, 'epsilon', 100);
            gravity = GetFastValue(config, 'gravity', 50);
        }
        else
        {
            if (x === undefined) { x = 0; }
            if (y === undefined) { y = 0; }
            if (power === undefined) { power = 0; }
            if (epsilon === undefined) { epsilon = 100; }
            if (gravity === undefined) { gravity = 50; }
        }

        super(x as number, y, true);

        this._gravity = gravity!;
        this._power = power! * gravity!;
        this._epsilon = epsilon! * epsilon!;
    }

    override update (particle: AnyParticle, delta: number): void
    {
        var x = this.x - particle.x;
        var y = this.y - particle.y;
        var dSq = x * x + y * y;

        if (dSq === 0)
        {
            return;
        }

        var d = Math.sqrt(dSq);

        if (dSq < this._epsilon)
        {
            dSq = this._epsilon;
        }

        var factor = ((this._power * delta) / (dSq * d)) * 100;

        particle.velocityX += x * factor;
        particle.velocityY += y * factor;
    }

    get epsilon (): number
    {
        return Math.sqrt(this._epsilon);
    }

    set epsilon (value: number)
    {
        this._epsilon = value * value;
    }

    get power (): number
    {
        return this._power / this._gravity;
    }

    set power (value: number)
    {
        this._power = value * this._gravity;
    }

    get gravity (): number
    {
        return this._gravity;
    }

    set gravity (value: number)
    {
        var pwr = this.power;

        this._gravity = value;
        this.power = pwr;
    }
}

export default GravityWell;
